/**
 * ArgemiroKioskView -- Kiosk de Argemiro para colaboradores (KIOSK-ARGEMIRO-COMPRA V1).
 * Mision PANEL-OPERATIONS-001 (ASSIGN bit #2966).
 *
 * El colaborador (rol client) se identifica con PIN y le HABLA a Argemiro para
 * registrar compras (foto de factura) o consultar. Darle credibilidad al agente:
 * la compra entra "via Argemiro", no como formulario plano.
 *
 * UI simple, sin switches Chat|RAG|RAG+AI (a diferencia de #/agents). Todo
 * client-side: reusa
 *   - AgentApiClient.sendChat  (chat real, webhook router-v0-1 / Patronus v1.9)
 *   - ComprasApiClient.uploadOcr (OCR factura, workflow Purchase V1.0 — T1)
 *   - OperadorPin (gate + trazabilidad del colaborador)
 *   - SlashCommands (/factura)
 *
 * Aislamiento de memoria (T3): session_id namespaced kiosk-{businessId}-{operadorId}
 * para no contaminar la memoria del chat de #/agents.
 *
 * NO toca workflows n8n ni capacidades de Argemiro en BD
 * (INS-ARGEMIRO-CHANGE-PROTOCOL no se dispara).
 *
 * @module presentation/views/ArgemiroKioskView
 */

// SIN-VUELTA: es un kiosco a pantalla completa para el colaborador. Un enlace al panel del dueno seria una puerta que no le toca abrir, y el kiosco existe justamente para que no la vea.
// Lo declara la pantalla y no una lista en el build: la razon tiene que estar
// donde alguien la lea el dia que se pregunte por que esta no tiene salida.

import { AgentApiClient } from '../../infrastructure/api/AgentApiClient.js';
import { ComprasApiClient } from '../../infrastructure/api/ComprasApiClient.js';
import { InsumosApiClient } from '../../infrastructure/api/InsumosApiClient.js';
import { PorcionadoApiClient } from '../../infrastructure/api/PorcionadoApiClient.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { getOperador, setupOperadorPin } from '../components/OperadorPin.js';
import { parseSlashCommand } from '../components/SlashCommands.js';
import { errorMessage, errorStatus } from '../../domain/value-objects/CrudResult.js';
import { parsearNumeroColombiano } from '../shared/numeroColombiano.js';
import { marcadoSinInventario, sugerirSinInventario, textoErrorSinInventario, rechazoDeAprobar } from '../../application/compras/sinInventario.js';
// OCR-DUP-1 (COORD #5438): deteccion de doble captura al aprobar factura.
import { FinanzasApiClient } from '../../infrastructure/api/FinanzasApiClient.js';
import { fechaOperativaBogota } from '../shared/muestraAleatoria.js';
// COSTO-ALERTA-P-T1 (bit #5794): costo de referencia del catalogo para el delta.
import { InventarioApiClient } from '../../infrastructure/api/InventarioApiClient.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { formatearNumero } from '../shared/numeroColombiano.js';
const firstName = (n) => String(n || '').trim().split(/\s+/)[0] || '';
// OCR-T1 (bit #5260): antes maximumFractionDigits:0 escondia los centavos y el
// colaborador no podia ver por que un subtotal no cuadraba. Entero = limpio;
// con fraccion = sus 2 decimales visibles.
const fmtCOP = (n) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  const dec = Number.isInteger(v) ? 0 : 2;
  return formatearNumero(v, { style: 'currency', currency: 'COP', minimumFractionDigits: dec, maximumFractionDigits: dec });
};

/**
 * Extrae texto legible de la respuesta del chat n8n. Mismo orden de campos que
 * AgentListView.renderSmartResponse: reply|output|text|message (defensivo).
 */
function extractReply(data) {
  if (!data) return 'Listo. 👍';
  if (typeof data === 'string') return esc(data).replace(/\n/g, '<br>');
  const txt = data.reply || data.output || data.text || data.message
    || data?.content?.parts?.[0]?.text || data?.data?.reply || '';
  if (txt) return esc(String(txt)).replace(/\n/g, '<br>');
  return 'Listo. 👍';
}

// ============== Render principal (router entry) ==============

export function renderArgemiroKiosk(container) {
  const businessId = getSelectedBusiness();
  const businessName = getSelectedBusinessName();

  const $pt = document.getElementById('nx-page-title');
  if ($pt) $pt.textContent = 'Argemiro';

  if (!businessId) {
    container.innerHTML = `
      <section class="kiosk-argemiro" style="max-width:560px;margin:40px auto;padding:24px;text-align:center;color:#8b949e">
        <div style="font-size:2.4rem;margin-bottom:8px">🧙</div>
        <p>Selecciona un negocio para abrir Argemiro.</p>
      </section>`;
    return;
  }

  const op = getOperador();
  if (!op) {
    renderGate(container, businessName);
    return;
  }

  renderChat(container, { businessId, businessName, op });
}

// ============== Gate de identificacion (OperadorPin) ==============

function renderGate(container, businessName) {
  container.innerHTML = `
    <section class="kiosk-argemiro" style="max-width:520px;margin:40px auto;padding:24px;text-align:center">
      <div style="font-size:3rem;margin-bottom:6px">🧙</div>
      <h1 style="font-size:1.35rem;color:#f0f6fc;margin:0 0 2px">Argemiro</h1>
      <p style="color:#8b949e;font-size:0.875rem;margin:0 0 24px">${esc(businessName || 'Tu negocio')}</p>
      <div style="background:#161b22;border:1px solid #30363d;border-radius:14px;padding:28px 22px">
        <p style="color:#c9d1d9;font-size:0.95rem;margin:0 0 18px">Identifícate con tu PIN para hablar con Argemiro.</p>
        <div id="nx-operador-wrap" style="display:flex;justify-content:center"></div>
      </div>
    </section>`;

  setupOperadorPin();

  // Al identificarse (OperadorPin emite 'operador:changed') -> re-render a chat.
  const onChanged = () => {
    window.removeEventListener('operador:changed', onChanged);
    renderArgemiroKiosk(container);
  };
  window.addEventListener('operador:changed', onChanged);
}

// ============== Chat kiosk ==============

function renderChat(container, { businessId, businessName, op }) {
  // T3: aislar memoria del chat general de #/agents.
  const sessionId = `kiosk-${businessId}-${op.id}`;

  container.innerHTML = `
    <section class="kiosk-argemiro" style="max-width:680px;margin:0 auto;display:flex;flex-direction:column;height:calc(100vh - 140px);min-height:420px">
      <header style="display:flex;align-items:center;gap:10px;padding:12px 4px;border-bottom:1px solid #21262d">
        <div style="font-size:1.6rem">🧙</div>
        <div style="flex:1;min-width:0">
          <div style="font-size:1rem;color:#f0f6fc;font-weight:600">Argemiro</div>
          <div style="font-size:0.875rem;color:#8b949e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(businessName || '')} · ${esc(op.nombre)}</div>
        </div>
        <div id="nx-operador-wrap"></div>
      </header>

      <div id="ka-messages" role="log" aria-live="polite" style="flex:1;overflow-y:auto;padding:16px 4px;display:flex;flex-direction:column;gap:12px"></div>

      <div style="padding:10px 4px 4px">
        <div style="display:flex;gap:8px;margin-bottom:10px">
          <button id="ka-upload-btn" type="button"
            style="flex:1;padding:14px;background:#1f6feb;border:none;border-radius:10px;color:#fff;font-size:0.95rem;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px">
            🧾 Subir factura
          </button>
          <button id="ka-desperdicio-btn" type="button"
            style="flex:1;padding:14px;background:#d29922;border:none;border-radius:10px;color:#1a1300;font-size:0.95rem;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px">
            📦 Movimientos
          </button>
        </div>
        <button id="ka-porcionado-btn" type="button"
          style="width:100%;padding:12px;background:#a371f7;border:none;border-radius:10px;color:#1a0d2e;font-size:0.9rem;font-weight:600;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:10px">
          🍖 Pendientes de porcionar
        </button>
        <div style="display:flex;gap:8px;align-items:flex-end">
          <textarea id="ka-input" rows="1" placeholder="Escríbele a Argemiro…  (o /factura)"
            style="flex:1;resize:none;padding:12px;background:#0d1117;border:1px solid #30363d;border-radius:10px;color:#e1e4e8;font-size:0.9rem;max-height:120px;line-height:1.4"></textarea>
          <button id="ka-send" type="button" aria-label="Enviar"
            style="padding:12px 16px;background:#238636;border:none;border-radius:10px;color:#fff;font-weight:700;cursor:pointer;font-size:1rem">➤</button>
        </div>
      </div>
      <input id="ka-file" type="file" accept="image/png,image/jpeg,image/webp,application/pdf" multiple hidden>
    </section>`;

  setupOperadorPin(); // badge del operador en el header

  const $messages = container.querySelector('#ka-messages');
  const $input = container.querySelector('#ka-input');
  const $send = container.querySelector('#ka-send');
  const $uploadBtn = container.querySelector('#ka-upload-btn');
  const $desperdicioBtn = container.querySelector('#ka-desperdicio-btn');
  const $porcionadoBtn = container.querySelector('#ka-porcionado-btn');
  const $file = container.querySelector('#ka-file');

  // Bienvenida
  appendAgent($messages, `¡Hola ${esc(firstName(op.nombre) || op.nombre)}! 👋 Soy <b>Argemiro</b>. Puedo registrar tus compras: toca <b>🧾 Subir factura</b> o escribe <code>/factura</code> y mándame la foto. También puedes preguntarme lo que necesites.`);

  // ---------- Chat (T3) ----------
  async function sendMessage() {
    const text = $input.value.trim();
    if (!text) return;

    // Comando /factura (o alias compra/recibo) -> abrir captura, sin ir a n8n.
    const cmd = parseSlashCommand(text);
    if (cmd && Array.isArray(cmd.command?.alias) && cmd.command.alias.includes('factura')) {
      resetInput();
      appendUser($messages, esc(text));
      appendAgent($messages, 'Perfecto 📸 elige la foto de tu factura…');
      $file.click();
      return;
    }
    // KIOSK-DESPERDICIO-V1 (bit #3021): /desperdicio (o alias merma/dano/movimiento)
    // -> stepper conversacional propio, sin pasar por n8n.
    if (cmd && Array.isArray(cmd.command?.alias) && cmd.command.alias.includes('desperdicio')) {
      resetInput();
      appendUser($messages, esc(text));
      startDesperdicioFlow($messages, businessId);
      return;
    }

    resetInput();
    appendUser($messages, esc(text));
    const $typing = appendTyping($messages);
    const res = await AgentApiClient.sendChat(text, sessionId, { narrative: 'natural' });
    $typing.remove();
    if (res.ok) {
      appendAgent($messages, extractReply(res.data));
    } else {
      appendAgent($messages, 'Uy, no pude responder ahora mismo. Intenta de nuevo en un momento. 🙏');
    }
  }

  function resetInput() {
    $input.value = '';
    $input.style.height = 'auto';
    $input.disabled = false;
    $input.focus();
  }

  $send.addEventListener('click', sendMessage);
  $input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  });
  $input.addEventListener('input', () => {
    $input.style.height = 'auto';
    $input.style.height = Math.min($input.scrollHeight, 120) + 'px';
  });

  // ---------- Registrar movimiento (KIOSK-DESPERDICIO-V1, renombrado MK-T1) ----------
  $desperdicioBtn.addEventListener('click', () => {
    appendUser($messages, '📦 Quiero registrar un movimiento');
    startDesperdicioFlow($messages, businessId, sessionId);
  });

  // ---------- Pendientes de porcionar (PORC-P1, bit #3153) ----------
  $porcionadoBtn.addEventListener('click', () => {
    appendUser($messages, '🍖 Ver pendientes de porcionar');
    startPorcionadoFlow($messages, businessId);
  });

  // ---------- Subir factura (T4 + T5) ----------
  $uploadBtn.addEventListener('click', () => $file.click());
  $file.addEventListener('change', async () => {
    const files = Array.from($file.files || []);
    $file.value = '';
    if (files.length) await handleFacturas(files);
  });

  async function handleFacturas(files) {
    const opNow = getOperador() || op; // T5: operador verificado al momento de subir
    appendUser($messages, files.length === 1 ? '📎 Envié una factura' : `📎 Envié ${files.length} facturas`);

    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      const prefijo = files.length > 1 ? `(${i + 1}/${files.length}) ` : '';
      const $typing = appendTyping($messages, `${prefijo}Leyendo tu factura…`);

      const up = await ComprasApiClient.uploadOcr(businessId, f, {
        operadorId: opNow.id,
        operadorNombre: opNow.nombre,
        source: 'kiosk-argemiro'
      });
      $typing.remove();

      if (up.ok) {
        const lineas = [];
        if (up.proveedor) lineas.push(`Proveedor: <b>${esc(up.proveedor)}</b>`);
        lineas.push(`Total: <b>${fmtCOP(up.total)}</b>`);
        lineas.push(`Items detectados: <b>${up.itemsCount}</b>`);
        appendAgent($messages, `✅ ¡Listo! ${prefijo ? esc(prefijo) : ''}Recibí tu factura.<br>${lineas.join('<br>')}`);

        // FV-T4 (bit #3363): verificacion conversacional. Antes se cerraba aqui con
        // "pendiente de aprobacion del administrador" -- y nadie revisaba lo que el
        // OCR habia entendido (caso Badur: 5 und cuando la caja traia 8).
        // startVerificacionFlow decide solo si el backend soporta la feature; si no,
        // pinta ese mismo mensaje clasico. Ver nota de disponibilidad alli.
        await startVerificacionFlow($messages, businessId, up.facturaId, opNow);
      } else if (up.status === 0) {
        appendAgent($messages, `❌ ${esc(prefijo)}No hay conexión ahora mismo. Verifica el internet e inténtalo de nuevo. 🙏`);
      } else {
        appendAgent($messages, `❌ ${esc(prefijo)}No pude leer bien esta factura. ¿Puedes tomar la foto más clara y volver a enviarla? 📸`);
      }
    }
  }

  setTimeout(() => $input.focus(), 60);
}

// ============== Flujo Movimientos: Desperdicio/Dano/Aumento (KIOSK-DESPERDICIO-V1,
// bit #3021; renombrado + tipo Aumento agregado en MK-T1, bit #3257) ==============
// Stepper conversacional: insumo -> tipo -> cantidad -> nota -> confirmar. Cada paso
// deshabilita el bubble anterior (evita doble-submit) y encadena el siguiente, igual
// patron de credibilidad que /factura (todo dentro del chat, sin modales nativos).

function fmtQty(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return String(n);
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** MOVM-2 (bit #3022): mismo dedupe defensivo por nombre que ComprasView -- el
 * sync_uuid no viaja en este GET y la re-migracion duplico insumos. */
function dedupeInsumosByNombre(items) {
  const seen = new Map();
  for (const i of items) {
    const key = String(i.nombre_normalizado || i.nombre || i.insumo_id).trim().toLowerCase();
    if (!seen.has(key)) seen.set(key, i);
  }
  return [...seen.values()].sort((a, b) =>
    String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', { sensitivity: 'base' }));
}

/** Restriccion del ASSIGN: "Solo insumos de los grupos del operador" (titular+reemplazo).
 * InsumosController no expone grupoId por insumo (solo texto Grupo) -> match tolerante
 * por NOMBRE contra colaborador.grupoConteoNombre/grupoConteoReemplazoNombre (mismo
 * patron SR-T4/MOV-6a del resto del ecosistema). Si el operador no tiene grupo asignado
 * (sesion vieja pre-fix, o colaborador sin asignar) no se bloquea: se muestra todo. */
function insumoPerteneceAGrupo(insumo, operador) {
  const g1 = String(operador.grupo || '').trim().toLowerCase();
  const g2 = String(operador.grupoReemplazo || '').trim().toLowerCase();
  if (!g1 && !g2) return true;
  const ig = String(insumo.grupo || '').trim().toLowerCase();
  return ig === g1 || (!!g2 && ig === g2);
}

async function startDesperdicioFlow($messages, businessId) {
  const operador = getOperador();
  if (!operador) {
    appendAgent($messages, 'Necesito que te identifiques primero para registrar el desperdicio. 🔐');
    return;
  }

  const $typing = appendTyping($messages, 'Buscando tus insumos…');
  const [insumosRes, entradasRes] = await Promise.all([
    // OPS-MOV-P1 (bit #3293): searchAllInsumosWithSyncUuid en vez de searchAllInsumos
    // -- el payload necesita el sync_uuid REAL (contrato #3261), no el PK Postgres.
    InsumosApiClient.searchAllInsumosWithSyncUuid(businessId),
    // T2: sugerencias -- insumos con ENTRADA hoy son candidatos naturales a desperdicio
    // post-proceso (caso real: 4000g camaron procesado). Defensivo: si falla, sin sugerencias.
    ComprasApiClient.listMovimientos(businessId, { tipo: 'entrada', from: todayISO(), to: todayISO(), pageSize: 20 })
      .catch(() => ({ ok: false }))
  ]);
  $typing.remove();

  const todos = (insumosRes.ok && Array.isArray(insumosRes.data?.items)) ? insumosRes.data.items : [];
  if (!insumosRes.ok || todos.length === 0) {
    appendAgent($messages, 'No pude cargar el catálogo de insumos ahora mismo. Intenta de nuevo en un momento. 🙏');
    return;
  }
  const delGrupo = dedupeInsumosByNombre(todos).filter(i => insumoPerteneceAGrupo(i, operador));

  if (delGrupo.length === 0) {
    appendAgent($messages, 'No encontré insumos asignados a tu grupo todavía. Pídele a tu administrador que revise tu asignación en Colaboradores. 🙏');
    return;
  }

  const entradasHoy = (entradasRes.ok && Array.isArray(entradasRes.data?.items)) ? entradasRes.data.items : [];
  const vistos = new Set();
  const sugerencias = [];
  for (const m of entradasHoy) {
    const match = delGrupo.find(i => String(i.insumo_id) === String(m.insumo_id));
    if (match && !vistos.has(match.insumo_id)) { vistos.add(match.insumo_id); sugerencias.push({ insumo: match, cantidadHoy: m.cantidad }); }
  }

  renderInsumoPicker($messages, businessId, operador, delGrupo, sugerencias);
}

function renderInsumoPicker($messages, businessId, operador, insumos, sugerencias) {
  const chipsHtml = sugerencias.length ? `
    <div style="margin-bottom:8px">
      <div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">📥 Con entradas hoy (candidatos a desperdicio):</div>
      <div style="display:flex;flex-wrap:wrap;gap:6px">
        ${sugerencias.map(s => `<button type="button" class="ka-desp-chip" data-iid="${esc(s.insumo.insumo_id)}"
          style="padding:6px 10px;background:#1c2a1c;border:1px solid #3fb950;border-radius:16px;color:#3fb950;font-size:0.875rem;cursor:pointer">
          ${esc(s.insumo.nombre)} <span style="opacity:0.7">(+${fmtQty(s.cantidadHoy)} hoy)</span>
        </button>`).join('')}
      </div>
    </div>` : '';

  const html = `${chipsHtml}
    <input type="text" class="ka-desp-search" placeholder="🔍 Buscar insumo…"
      style="width:100%;padding:8px 10px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:0.875rem;margin-bottom:8px;box-sizing:border-box">
    <div class="ka-desp-list" style="display:flex;flex-direction:column;gap:4px;max-height:220px;overflow-y:auto">
      ${insumos.map(i => `<button type="button" class="ka-desp-item" data-iid="${esc(i.insumo_id)}" data-nombre="${esc((i.nombre + ' ' + (i.presentacion || '')).toLowerCase())}"
        style="text-align:left;padding:8px 10px;background:#0d1117;border:1px solid #21262d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;cursor:pointer">
        ${esc(i.nombre)}${i.presentacion ? ` <span style="color:#6e7681">· ${esc(i.presentacion)}</span>` : ''} <span style="color:#6e7681">— ${esc(i.unidad)}</span>
      </button>`).join('')}
    </div>`;

  const $bubble = appendAgent($messages, `¿Qué insumo se desperdició? Elige de la lista o busca:<div style="margin-top:8px;min-width:240px">${html}</div>`);

  const $search = $bubble.querySelector('.ka-desp-search');
  const $items = [...$bubble.querySelectorAll('.ka-desp-item')];
  $search?.addEventListener('input', () => {
    const q = $search.value.trim().toLowerCase();
    $items.forEach(btn => { btn.style.display = (!q || btn.dataset.nombre.includes(q)) ? '' : 'none'; });
  });

  function pick(insumoId) {
    const insumo = insumos.find(i => String(i.insumo_id) === String(insumoId));
    if (!insumo) return;
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, `✅ ${esc(insumo.nombre)}`);
    renderTipoPicker($messages, businessId, operador, insumo);
  }
  $bubble.querySelectorAll('.ka-desp-item, .ka-desp-chip').forEach(btn => {
    btn.addEventListener('click', () => pick(btn.dataset.iid));
  });
}

function renderTipoPicker($messages, businessId, operador, insumo) {
  const html = `
    <div style="display:flex;gap:8px;margin-top:6px;flex-wrap:wrap">
      <button type="button" class="ka-desp-tipo" data-tipo="desperdicio" data-label="🗑️ Desperdicio" style="flex:1;min-width:90px;padding:10px;background:#2d2410;border:1px solid #d29922;border-radius:8px;color:#d29922;font-weight:600;cursor:pointer">🗑️ Desperdicio</button>
      <button type="button" class="ka-desp-tipo" data-tipo="dano" data-label="⚠️ Daño" style="flex:1;min-width:90px;padding:10px;background:#2d1414;border:1px solid #f85149;border-radius:8px;color:#f85149;font-weight:600;cursor:pointer">⚠️ Daño</button>
      <button type="button" class="ka-desp-tipo" data-tipo="aumento" data-label="➕ Aumento" style="flex:1;min-width:90px;padding:10px;background:#0d2818;border:1px solid #3fb950;border-radius:8px;color:#3fb950;font-weight:600;cursor:pointer">➕ Aumento</button>
    </div>`;
  const $bubble = appendAgent($messages, `¿Qué tipo de movimiento vas a registrar?${html}`);
  $bubble.querySelectorAll('.ka-desp-tipo').forEach(btn => {
    btn.addEventListener('click', () => {
      $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
      $bubble.style.opacity = '0.6';
      appendUser($messages, btn.dataset.label);
      renderCantidadPicker($messages, businessId, operador, insumo, btn.dataset.tipo);
    });
  });
}

function renderCantidadPicker($messages, businessId, operador, insumo, tipo) {
  const unidad = insumo.unidad || 'unidades';
  const html = `
    <div style="margin-top:6px;display:flex;gap:8px;align-items:center">
      <input type="text" inputmode="decimal" class="ka-desp-cantidad" placeholder="0"
        style="flex:1;padding:10px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:1rem;text-align:center;box-sizing:border-box">
      <span style="color:#8b949e;font-size:0.875rem;white-space:nowrap">${esc(unidad)}</span>
      <button type="button" class="ka-desp-cant-ok" style="padding:10px 16px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">➜</button>
    </div>`;
  const $bubble = appendAgent($messages, `¿Cuánta cantidad (en ${esc(unidad)})?${html}`);
  const $input = $bubble.querySelector('.ka-desp-cantidad');
  const $ok = $bubble.querySelector('.ka-desp-cant-ok');

  const submit = () => {
    // OCR-T1 (bit #5260): el replace de una sola coma convertia "1.234,56" en
    // NaN. Parser colombiano completo; el guard >0 rechaza NaN/invalido igual.
    const cantidad = parsearNumeroColombiano($input.value);
    if (!(cantidad > 0)) { $input.style.borderColor = '#f85149'; $input.focus(); return; }
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, `${fmtQty(cantidad)} ${esc(unidad)}`);
    renderNotaPicker($messages, businessId, operador, insumo, tipo, cantidad);
  };
  $ok.addEventListener('click', submit);
  $input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
  setTimeout(() => $input.focus(), 50);
}

function renderNotaPicker($messages, businessId, operador, insumo, tipo, cantidad) {
  const html = `
    <div style="margin-top:6px">
      <input type="text" class="ka-desp-nota" placeholder="Nota opcional (ej: se dañó en cocción)…" maxlength="140"
        style="width:100%;padding:8px 10px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:0.875rem;margin-bottom:8px;box-sizing:border-box">
      <div style="display:flex;gap:8px">
        <button type="button" class="ka-desp-nota-skip" style="flex:1;padding:9px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#8b949e;cursor:pointer">Omitir</button>
        <button type="button" class="ka-desp-nota-ok" style="flex:1;padding:9px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">Continuar ➜</button>
      </div>
    </div>`;
  const $bubble = appendAgent($messages, `¿Alguna nota? (opcional)${html}`);
  const $nota = $bubble.querySelector('.ka-desp-nota');

  const finish = (nota) => {
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, nota ? esc(nota) : '(sin nota)');
    renderConfirmacion($messages, businessId, operador, insumo, tipo, cantidad, nota);
  };
  $bubble.querySelector('.ka-desp-nota-skip').addEventListener('click', () => finish(''));
  $bubble.querySelector('.ka-desp-nota-ok').addEventListener('click', () => finish($nota.value.trim()));
  $nota.addEventListener('keydown', (e) => { if (e.key === 'Enter') finish($nota.value.trim()); });
  setTimeout(() => $nota.focus(), 50);
}

// MV-T2 (bit #3349, canon Josse 21-jul): el kiosk manda el code canonico
// 'desperdicio' (antes mandaba 'merma' con label Desperdicio -- semantica rota).
// Los codes legacy quedan como alias de lectura por si algun flujo viejo los pasa.
const TIPO_LABELS = { desperdicio: '🗑️ Desperdicio', merma: '🗑️ Desperdicio', dano: '⚠️ Daño', aumento: '➕ Aumento' };
const TIPO_NOUN = { desperdicio: 'desperdicio', merma: 'desperdicio', dano: 'daño', aumento: 'aumento' };
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// MV-T5 (bit #3391): compara la cantidad contra lo que ese insumo suele
// desperdiciar y, si se pasa, lo dice en el resumen.
//
// LIMITACION CONOCIDA Y DELIBERADA: el endpoint MV-T4 (contrato #3393) expone el
// promedio SEMANAL, no el promedio por movimiento (totalesPorTipo es suma de
// cantidades, no conteo -- verificado en el DTO de nexo-api #125). Por eso el
// aviso compara contra "lo de una semana entera" y el texto lo dice tal cual, en
// vez de fingir una precision que el dato no tiene. Consecuencia: un desperdicio
// grande en un insumo que se desperdicia MUY seguido puede no avisar (falso
// negativo). Se prefiere callar antes que mentir; el caso queda propuesto para V2
// como desperdicioPromedioPorMovimiento.
async function avisarSiInusual($bubble, businessId, insumo, tipo, cantidad) {
  if (tipo !== 'desperdicio' && tipo !== 'merma') return;
  const $slot = $bubble.querySelector('.ka-desp-aviso');
  if (!$slot) return;

  const res = await ComprasApiClient
    .getEstadisticasMovimientos(businessId, { insumoId: insumo.insumo_id })
    .catch(() => null);
  if (!res?.ok || res.disponible === false) return;

  const fila = (res.data?.insumos || []).find(i => String(i.insumo_id) === String(insumo.insumo_id));
  const umbral = fila?.umbral_anomalia;
  if (!(umbral > 0) || !(Number(cantidad) > umbral)) return;

  const prom = fila.desperdicio_promedio_semanal;
  const unidad = insumo.unidad ? ` ${esc(insumo.unidad)}` : '';
  $slot.innerHTML = `
    <div style="margin-top:8px;padding:9px 11px;background:#2d2410;border:1px solid #d29922;border-radius:8px;color:#d29922;font-size:0.875rem;line-height:1.45">
      🤔 Es más de lo que se suele desperdiciar de <b>${esc(insumo.nombre)}</b> en una semana entera
      (unos ${fmtQty(prom)}${unidad}).
      <div style="color:#c9d1d9;font-size:0.875rem;margin-top:3px">Si es correcto, confirma tranquilo. Si te equivocaste, cancela y vuelve a empezar.</div>
    </div>`;
}

function renderConfirmacion($messages, businessId, operador, insumo, tipo, cantidad, nota) {
  const tipoLabel = TIPO_LABELS[tipo] || TIPO_LABELS.desperdicio;
  const resumen = `
    <div style="background:#0d1117;border:1px solid #30363d;border-radius:8px;padding:10px;margin-top:6px;font-size:0.875rem;line-height:1.6">
      <div><b>${esc(insumo.nombre)}</b>${insumo.presentacion ? ` · ${esc(insumo.presentacion)}` : ''}</div>
      <div>${tipoLabel} · ${fmtQty(cantidad)} ${esc(insumo.unidad || '')}</div>
      ${nota ? `<div style="color:#8b949e">📝 ${esc(nota)}</div>` : ''}
    </div>
    <div class="ka-desp-aviso"></div>
    <div style="display:flex;gap:8px;margin-top:8px">
      <button type="button" class="ka-desp-cancel" style="flex:1;padding:10px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#8b949e;cursor:pointer">Cancelar</button>
      <button type="button" class="ka-desp-confirm" style="flex:1;padding:10px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">✅ Confirmar</button>
    </div>`;
  const $bubble = appendAgent($messages, `Resumen — revisa antes de confirmar:${resumen}`);

  // MV-T5 (bit #3391): aviso suave si la cantidad se sale de lo habitual.
  // NUNCA bloquea (regla del ASSIGN #3390): el boton Confirmar sigue activo; esto
  // solo hace pensar antes de un dedazo. Va async para no frenar el flujo en la
  // tablet de cocina, y si falla no se muestra nada.
  avisarSiInusual($bubble, businessId, insumo, tipo, cantidad);

  $bubble.querySelector('.ka-desp-cancel').addEventListener('click', () => {
    $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, '❌ Cancelar');
    appendAgent($messages, 'Sin problema, no registré nada. ¿Necesitas algo más? 🙂');
  });

  $bubble.querySelector('.ka-desp-confirm').addEventListener('click', async () => {
    // MK-T2 (bit #3257): guardia dura ANTES de postear. El movimiento "fantasma"
    // reportado en prod (usuario="Panel", insumo="(sin insumo)") viene de un id o
    // nombre vacio/invalido llegando al backend sin fallar -- Desktop lo aplica
    // igual (sin match) en vez de rechazarlo. Bloquear aqui es mas barato que
    // depurar un movimiento fantasma despues.
    if (!insumo.insumo_id || !UUID_RE.test(String(insumo.insumo_id))) {
      appendAgent($messages, '❌ Este insumo no tiene un identificador valido (posible catalogo desactualizado). Cierra el chat, recarga la pagina y vuelve a intentar. 🙏');
      return;
    }
    if (!operador.nombre || !operador.nombre.trim()) {
      appendAgent($messages, '❌ No pude confirmar tu identidad (nombre vacio). Sal e identifícate de nuevo con tu PIN antes de registrar el movimiento. 🔐');
      return;
    }

    $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, '✅ Confirmar');
    const $typing = appendTyping($messages, 'Registrando…');

    // MOVM-1 (bit #3022): usuario + hora reales -- mismo contrato que el form
    // manager de Compras Tab4, para que MOV-6/7 de Desktop bajen el movimiento completo.
    const now = new Date();
    const hora = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
    const payload = {
      tipo,
      insumoId: insumo.insumo_id,
      cantidad,
      fecha: todayISO(),
      hora,
      usuario: operador.nombre,
      // MK-T2 (bit #3257, contrato confirmado Argy-D bit #3261): operador.id
      // (OperadorPin) es el uuid del colaborador -- atribucion canonica.
      colaboradorUuid: operador.id || null,
      // OPS-MOV-P1 (bit #3293, contrato #3261): sync_uuid REAL del insumo (no el
      // PK Postgres que se mandaba antes) + nombre como defensa doble (fallback
      // match por nombre / auto-create lazy si el uuid no resuelve, del lado Desktop).
      insumoSyncId: insumo.sync_uuid || null,
      insumoNombre: insumo.nombre || null,
      motivo: nota ? `[Kiosk] ${nota}` : `[Kiosk] Registrado por ${operador.nombre}`
    };
    const res = await ComprasApiClient.createMovimiento(businessId, payload);
    $typing.remove();

    if (res.ok) {
      appendAgent($messages,
        `✅ ¡Listo! Registré el ${TIPO_NOUN[tipo] || 'movimiento'} de <b>${esc(insumo.nombre)}</b> ` +
        `(${fmtQty(cantidad)} ${esc(insumo.unidad || '')}). ¡Gracias ${esc(firstName(operador.nombre))}! 🙌`);
    } else if (errorStatus(res) === 409) {
      // Duplicado: ofrecer rehacer la nota es lo unico util aqui, porque cambiarla
      // es precisamente lo que desbloquea el registro (la nota entra en la clave
      // de deduplicacion del backend).
      renderDuplicado($messages, res, () =>
        renderNotaPicker($messages, businessId, operador, insumo, tipo, cantidad));
    } else {
      appendAgent($messages, mensajeErrorMovimiento(res));
    }
  });
}

// MV-T2b (bit #3442): antes esto mostraba "(error)" pelado y el colaborador no
// sabia si reintentar, corregir algo, o llamar al administrador. El mensaje del
// backend AHORA si se lee -- viajaba en `error` como string y el codigo hacia
// `error?.message`, que sobre un string es undefined.
function mensajeErrorMovimiento(res, contexto = '') {
  const status = errorStatus(res);
  const detalle = errorMessage(res, '');
  const extra = detalle ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:4px">${esc(detalle)}</div>` : '';

  if (status === 0 || res?.code === 'NETWORK' || res?.code === 'TIMEOUT') {
    return `❌ No hay conexión ahora mismo. No registré nada; inténtalo de nuevo en un momento. 🙏`;
  }
  if (status === 401 || res?.code === 'UNAUTHORIZED') {
    return `🔐 Tu sesión venció. Sal e identifícate de nuevo con tu PIN para registrar el movimiento.`;
  }
  return `❌ No pude registrar el movimiento.${contexto ? ' ' + contexto : ''} Intenta de nuevo en un momento. 🙏${extra}`;
}

// El 409 VIEJO (API sin MV-T6) devuelve un detail tecnico, con el uuid del insumo
// crudo: "Movimiento duplicado: ya existe uno con tipo=AUMENTO+referencia=[Kiosk]
// Cocino para insumo 370f8004-34f2-...". Mostrar ESO tal cual en burbuja grande es
// peor que el "(error)" que veniamos arreglando.
// Riesgo detectado por Argy-A en la coordinacion de merge (bitacora #3451): si el
// panel sale antes que api#126, el colaborador ve UUIDs. En vez de depender del
// orden de despliegue, se detecta el formato viejo y se cae al texto propio.
const DETAIL_TECNICO_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-|tipo=|referencia=|insumo_id/i;

// Duplicado (409), contrato MV-T6 congelado en bitacora #3446.
// El backend redacta el `detail` en lenguaje de kiosk, con el nombre del insumo y
// la fecha ("Ya registraste un AUMENTO de X Arroz con esta misma nota hoy..."), y
// el contrato pide mostrarlo TAL CUAL: reescribirlo aqui solo lo empeoraria y
// duplicaria el texto. Ambar y no rojo porque en la mayoria de los casos es doble
// clic y el movimiento SI quedo guardado -- no es una perdida.
function renderDuplicado($messages, res, onCambiarNota) {
  const crudo = errorMessage(res, '');
  const detalle = (crudo && !DETAIL_TECNICO_RE.test(crudo))
    ? crudo
    : 'Ya registraste un movimiento igual hoy. Si fue doble clic, ya quedó guardado; si es uno distinto, cámbiale la nota.';
  const $bubble = appendAgent($messages, `
    <div style="padding:9px 11px;background:#2d2410;border:1px solid #d29922;border-radius:8px;color:#d29922;font-size:0.875rem;line-height:1.45">
      🤔 ${esc(detalle)}
    </div>
    <div style="display:flex;gap:8px;margin-top:8px">
      <button type="button" class="ka-dup-listo" style="flex:1;padding:9px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#8b949e;cursor:pointer">Era eso, gracias</button>
      <button type="button" class="ka-dup-nota" style="flex:1;padding:9px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">✏️ Cambiar la nota</button>
    </div>`);

  const cerrar = () => {
    $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
  };
  $bubble.querySelector('.ka-dup-listo').addEventListener('click', () => {
    cerrar();
    appendUser($messages, 'Era eso, gracias');
    appendAgent($messages, 'Listo 👍 ¿Necesitas algo más?');
  });
  $bubble.querySelector('.ka-dup-nota').addEventListener('click', () => {
    cerrar();
    appendUser($messages, '✏️ Cambiar la nota');
    onCambiarNota();
  });
}

// ============== Bandeja Pendientes de porcionar (PORC-P1, bit #3153) ==============
// Insumo comprado por peso (ej. pollo) que la fn v6 de Argy-A NO pudo convertir
// automaticamente a porciones (config sin tamano estructurado) -> queda con
// alerta porcionado_pendiente. El colaborador cuenta las porciones REALES y las
// registra: POST movimiento ENTRADA (ya soportado, #159) + PATCH cierra la alerta
// (PorcionadoApiClient, contrato leido del diff real de nexo-api PR #120).

async function startPorcionadoFlow($messages, businessId) {
  const operador = getOperador();
  if (!operador) {
    appendAgent($messages, 'Necesito que te identifiques primero para ver los pendientes de porcionar. 🔐');
    return;
  }

  const $typing = appendTyping($messages, 'Buscando pendientes de porcionar…');
  const [pendRes, insumosRes] = await Promise.all([
    PorcionadoApiClient.listPendientes(businessId),
    InsumosApiClient.searchAllInsumos(businessId)
  ]);
  $typing.remove();

  if (!pendRes.ok) {
    appendAgent($messages, 'No pude cargar los pendientes de porcionar ahora mismo. Intenta de nuevo en un momento. 🙏');
    return;
  }

  // El GET /porcionado-pendiente NO expone el grupo del insumo (solo insumoId)
  // -> se resuelve cruzando contra el catalogo canonico, mismo patron que
  // KIOSK-GRUPOS-FIX (bit #3093/#3094): texto de grupo, no id (evita la
  // colision de numeracion Desktop-vs-panel).
  const grupoPorInsumoId = new Map();
  if (insumosRes.ok) {
    for (const i of (insumosRes.data?.items || [])) grupoPorInsumoId.set(String(i.insumo_id), i.grupo || '');
  }

  const sinInsumoVinculado = pendRes.items.filter(p => !p.insumoId).length;
  let items = pendRes.items.filter(p => p.insumoId);
  items = items.map(p => ({ ...p, _grupo: grupoPorInsumoId.get(String(p.insumoId)) || '' }));

  const g1 = String(operador.grupo || '').trim().toLowerCase();
  const g2 = String(operador.grupoReemplazo || '').trim().toLowerCase();
  if (g1 || g2) {
    items = items.filter(p => {
      const ig = String(p._grupo || '').trim().toLowerCase();
      return ig === g1 || (!!g2 && ig === g2);
    });
  }

  if (items.length === 0) {
    const nota = sinInsumoVinculado > 0
      ? ` (${sinInsumoVinculado} pendiente(s) sin insumo vinculado — contacta al administrador)`
      : '';
    appendAgent($messages, `🎉 No tienes pendientes de porcionar en tu grupo.${nota}`);
    return;
  }

  renderPorcionadoPicker($messages, businessId, operador, items);
}

// ============== Flujo Verificacion de factura (FV-T4, bit #3363) ==============
// Tras el OCR, el colaborador revisa item por item lo que Argemiro entendio y lo
// corrige ANTES de que la factura entre al inventario. Caso fundacional (Badur
// 1318625): el OCR leyo 5 unidades de Coca Cola cuando la caja trae 8 -- entraban
// 5 al stock y el costo unitario quedaba inflado 8x.
//
// Contrato del POST: bitacora #3362 (congelado por Argy-A).
// Se manda cantidadFactura + factorEmpaque y el BACKEND deriva la cantidad, para
// que el colaborador nunca haga la conversion mental.

// El precio unitario se recalcula con SUBTOTAL ANCLADO: el subtotal es lo que
// cobro el proveedor (dato duro del papel) y no se toca; lo que cambia es el costo
// por unidad. Misma formula que el panel (FV-T5) y que el backend (#3362 usa
// AwayFromZero justamente para no separarse de este Math.round en los .5).
function precioUnitAnclado(subtotal, cantidad) {
  const s = Number(subtotal), c = Number(cantidad);
  if (!(c > 0) || !(s > 0)) return null;
  return Math.round((s / c) * 100) / 100;
}

// OCR-IVA-T1 (regla de Josse #5446): el costo REAL del negocio es CON IVA.
// costo_u = subtotal_linea x (1 + iva%/100) / unidades. El subtotal documental
// de la factura (sin IVA) NO se toca — es el ancla del papel; el IVA se aplica
// en los bordes (lo que ve el colaborador aqui y lo que viaja al Desktop via
// trigger v3). Caso real BADUR: 45.546 x 1.19 / 24 = $2.258,32 — con el costo
// sin IVA ($1.897,75) casi se vende a perdida.
function costoUnitConIva(subtotal, ivaPct, cantidad) {
  const s = Number(subtotal), c = Number(cantidad), iva = Number(ivaPct) || 0;
  if (!(c > 0) || !(s > 0)) return null;
  return Math.round((s * (1 + iva / 100) / c) * 100) / 100;
}

// Subtotal de linea CON IVA (para la validacion contra el NETO de la factura).
function subtotalConIva(subtotal, ivaPct) {
  const s = Number(subtotal) || 0, iva = Number(ivaPct) || 0;
  return s * (1 + iva / 100);
}

// Cantidad real que entra al inventario segun lo que dice el papel.
function cantidadDerivada(cantFactura, factor) {
  const cf = Number(cantFactura), fx = Number(factor);
  if (!(cf > 0) || !(fx > 0)) return null;
  return Math.round(cf * fx * 1000) / 1000;
}

async function startVerificacionFlow($messages, businessId, facturaId, operador) {
  // Mensaje clasico de siempre: se usa cuando la verificacion no aplica (sin
  // facturaId, backend sin FV-T1/FV-T2, o GET caido). Nunca dejamos al colaborador
  // sin saber que paso con su factura.
  const cerrarSinVerificacion = (motivo) => {
    appendAgent($messages,
      `<span style="color:#d29922">📋 Queda <b>pendiente de aprobación del administrador</b>. Él la revisa y la confirma. ¡Gracias ${esc(firstName(operador?.nombre))}! 🙌</span>` +
      (motivo ? `<div style="font-size:0.875rem;color:#6e7681;margin-top:4px">${esc(motivo)}</div>` : ''));
  };

  // OCR-FACT-REVIEW (mapa bit #4427): estas 3 salidas degradaban en SILENCIO al
  // mensaje generico — sin log no habia forma de diagnosticar cual disparo el
  // "no se ve la factura" desde el navegador. El colaborador sigue viendo el
  // mensaje amable; el warn es para nosotros.
  if (!facturaId) {
    console.warn('[KioskVerif] sin factura_id en la respuesta del webhook purchase — no se puede abrir la verificacion');
    cerrarSinVerificacion('');
    return;
  }

  const $typing = appendTyping($messages, 'Revisando lo que entendí…');
  const det = await ComprasApiClient.getFacturaById(businessId, facturaId);
  $typing.remove();

  if (!det.ok || !Array.isArray(det.data?.items) || det.data.items.length === 0) {
    console.warn('[KioskVerif] GET detalle fallo o factura sin items', { facturaId, ok: det.ok, items: det.data?.items?.length ?? null, error: det.error });
    cerrarSinVerificacion('');
    return;
  }

  // DISPONIBILIDAD (patron RB-T3b, bit #3083): el propio dato dice si la feature
  // esta viva. verificacion_status llega null mientras la migracion FV-T1 no este
  // aplicada en produccion -- en ese caso NO ofrecemos aprobar, porque el POST
  // daria 404 y el colaborador veria un error que no puede resolver.
  if (det.data.verificacion_status == null) {
    console.warn('[KioskVerif] verificacion_status=null — backend sin migracion FV-T1/FV-T2 desplegada; se degrada a pendiente-de-admin', { facturaId });
    cerrarSinVerificacion('');
    return;
  }
  if (String(det.data.verificacion_status).toUpperCase() === 'APPROVED') {
    appendAgent($messages, '✅ Esta factura ya quedó verificada. ¡Gracias! 🙌');
    return;
  }

  // COSTO-ALERTA-P-T1 (bit #5794): costo de REFERENCIA por insumo desde el
  // catalogo (cache 60s del client). Tras rev 1205 (COSTO-VIVO), insumos.costo
  // es el ULTIMO costo de compra CON IVA (regla #5446) — comparable 1:1 contra
  // el costo con IVA de esta factura. FAIL-OPEN: sin catalogo no hay delta,
  // pero la verificacion sigue identica.
  const costoRefPorId = new Map();
  try {
    const catRes = await InventarioApiClient.getInsumosWithConfig(businessId);
    if (catRes.ok && Array.isArray(catRes.data)) {
      for (const ins of catRes.data) {
        const costo = Number(ins.Costo ?? ins.costo) || 0;
        if (ins.id && costo > 0) costoRefPorId.set(String(ins.id), costo);
      }
    }
  } catch { /* sin referencia: los deltas simplemente no se pintan */ }

  // Estado local de la revision: arranca con lo que entendio el OCR.
  const items = det.data.items.map(it => ({
    id: it.id,
    nombre: it.insumo_nombre || it.producto || it.descripcion_raw || '(sin nombre)',
    unidadInv: it.unidad || '',
    subtotal: Number(it.subtotal) || 0,
    cantidad: Number(it.cantidad) || 0,
    cantFactura: it.cantidad_factura != null ? Number(it.cantidad_factura) : null,
    unidadFactura: it.unidad_factura || '',
    factor: it.factor_empaque != null ? Number(it.factor_empaque) : null,
    precioUnit: Number(it.precio_unit) || 0,
    // OCR-IVA-T1: %IVA por linea (columna %Iva del OCR). El adapter ya lo traia
    // (iva_pct); el kiosk no lo consumia y el costo mostrado quedaba sin IVA.
    ivaPct: Number(it.iva_pct) || 0,
    // OCR-FLUJO-COLAB-T1 (bit #5593): match del OCR + candidatos, para que el
    // COLABORADOR elija el insumo por producto (antes solo lo veia el admin).
    insumoId: it.insumo_id || null,
    matchStatus: it.match_status || 'PENDING',
    candidatos: Array.isArray(it.candidatos) ? it.candidatos : [],
    // COSTO-ALERTA-P-T1: referencia del catalogo para el delta (null = sin ref).
    costoRef: it.insumo_id ? (costoRefPorId.get(String(it.insumo_id)) || null) : null,
    matchCorregido: false,
    corregido: false,
    // COMPRAS-BORRADOR-ANULACION-T1: marca de una persona (cuenta como resuelto) y la sugerencia del OCR (no resuelve).
    sinInventario: marcadoSinInventario(it),
    sinInventarioCambiado: false,
    sugerirSinInventario: sugerirSinInventario(it)
  }));

  renderVerificacionResumen($messages, businessId, facturaId, operador, det.data, items);
}

/**
 * COSTO-ALERTA-P-T1 (bit #5794): linea de delta del costo nuevo (con IVA) vs el
 * ultimo costo de compra del catalogo. null/'' cuando no hay referencia o el
 * cambio esta bajo el umbral del 2% (ruido de redondeo no es alerta).
 */
function deltaCostoHtml(costoNuevo, costoRef) {
  const nuevo = Number(costoNuevo) || 0, ref = Number(costoRef) || 0;
  if (!(nuevo > 0) || !(ref > 0)) return '';
  const pct = (nuevo - ref) / ref * 100;
  if (Math.abs(pct) < 2) return '';
  const subio = pct > 0;
  const color = subio ? '#d29922' : '#3fb950';
  return `<div style="font-size:0.875rem;color:${color};margin-top:2px">${subio ? '▲ Subió' : '▼ Bajó'} ${Math.abs(pct).toFixed(1)}% vs tu última compra (${fmtCOP(ref)})</div>`;
}

function lineaItemHtml(it, idx) {
  const cant = `${fmtQty(it.cantidad)}${it.unidadInv ? ' ' + esc(it.unidadInv) : ''}`;
  // La conversion solo se muestra cuando existe y aporta (factor != 1): cubre
  // cajas (5 x8) y conversion de unidad (4 KG x1000), incluido factor fraccionario.
  const conv = (it.cantFactura > 0 && it.factor > 0 && it.factor !== 1)
    ? `<div style="font-size:0.875rem;color:#8b949e">La factura dice ${fmtQty(it.cantFactura)}${it.unidadFactura ? ' ' + esc(it.unidadFactura) : ''} &times;${fmtQty(it.factor)}</div>`
    : '';
  // OCR-IVA-T1: el costo por unidad se muestra CON IVA (el real del negocio).
  // El total de linea sigue siendo el del papel (sin IVA) — es el ancla que el
  // colaborador coteja contra la factura fisica.
  const puIva = costoUnitConIva(it.subtotal, it.ivaPct, it.cantidad);
  const costoHtml = puIva != null
    ? `${fmtCOP(puIva)} c/u${it.ivaPct > 0 ? ` <span style="color:#8b949e;font-size:0.875rem">(IVA ${fmtQty(it.ivaPct)}% incluido)</span>` : ''}`
    : `${fmtCOP(it.precioUnit)} c/u`;
  // COSTO-ALERTA-P-T1: delta vs el ultimo costo de compra (catalogo, con IVA).
  // Umbral 2%: el ruido de redondeo no es una alerta. Subio = ambar; bajo = verde.
  const deltaCosto = deltaCostoHtml(puIva, it.costoRef);
  // OCR-FLUJO-COLAB-T1: el match visible por producto. Verde = ya tiene insumo;
  // ambar = sin match, el colaborador lo elige (la primera vez el relaciona y
  // el sistema aprende via los hooks del backend al aprobar).
  const tieneMatch = !!it.insumoId;
  const matchLinea = it.sinInventario
    ? `<div data-ka-sin-inventario style="font-size:14px;color:#79c0ff;margin-top:2px">Sin inventario: es un flete o un servicio, no entra al inventario</div>`
    : tieneMatch
    ? `<div style="font-size:0.875rem;color:#3fb950;margin-top:2px">→ ${esc(it.nombre)}${it.matchCorregido ? ' <span style="font-size:0.875rem">(elegido por ti)</span>' : ''}</div>`
    : `<div style="font-size:0.875rem;color:#d29922;margin-top:2px">⚠ Sin insumo asignado — tócalo y elígelo</div>`;
  const sugerencia = (!tieneMatch && !it.sinInventario && it.sugerirSinInventario)
    ? `<div style="font-size:14px;color:#d29922;margin-top:2px">Parece un flete o un servicio: si no entra al inventario, toca Sin inventario.</div>`
    : '';
  // Un item con insumo no lleva el boton: la API rechaza marcar un item casado (409 ITEM_CASADO).
  const botonSinInv = (tieneMatch || it.matchStatus === 'MATCH') ? '' : `
        <button type="button" class="ka-ver-sininv" data-idx="${idx}" aria-pressed="${it.sinInventario ? 'true' : 'false'}"
          style="padding:6px 10px;background:${it.sinInventario ? '#1f6feb22' : '#161b22'};border:1px solid ${it.sinInventario ? '#1f6feb' : '#30363d'};border-radius:8px;color:#79c0ff;font-size:14px;cursor:pointer">${it.sinInventario ? 'Quitar sin inventario' : 'Sin inventario'}</button>`;
  return `
    <div style="padding:8px 10px;background:#0d1117;border:1px solid ${it.corregido || it.matchCorregido ? '#3fb950' : (tieneMatch ? '#21262d' : '#d2992255')};border-radius:8px;display:flex;gap:8px;align-items:center;justify-content:space-between">
      <div style="min-width:0;flex:1">
        <div style="font-size:0.875rem;color:#c9d1d9;font-weight:600">${esc(it.nombre)}${it.corregido ? ' <span style="color:#3fb950;font-size:0.875rem">✎ corregido</span>' : ''}</div>
        <div style="font-size:0.875rem;color:#c9d1d9">${cant} · ${costoHtml} · total ${fmtCOP(it.subtotal)}</div>
        ${deltaCosto}
        ${matchLinea}
        ${sugerencia}
        ${conv}
      </div>
      <div style="flex-shrink:0;display:flex;flex-direction:column;gap:4px">
        <button type="button" class="ka-ver-fix" data-idx="${idx}"
          style="padding:6px 10px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#79c0ff;font-size:0.875rem;cursor:pointer">Corregir</button>
        <button type="button" class="ka-ver-match" data-idx="${idx}"
          style="padding:6px 10px;background:${tieneMatch ? '#161b22' : '#d2992222'};border:1px solid ${tieneMatch ? '#30363d' : '#d29922'};border-radius:8px;color:${tieneMatch ? '#8b949e' : '#d29922'};font-size:0.875rem;cursor:pointer">${tieneMatch ? 'Cambiar' : 'Elegir'} insumo</button>${botonSinInv}
      </div>
    </div>`;
}

function renderVerificacionResumen($messages, businessId, facturaId, operador, factura, items) {
  const sumaItems = items.reduce((a, i) => a + (Number(i.subtotal) || 0), 0);
  const totalFactura = Number(factura.total) || 0;
  // OCR-IVA-T1: el sistema hace la cuenta del IVA solo (antes el aviso amarillo
  // le decia "puede ser IVA" al colaborador y lo dejaba con la duda). La suma
  // de (subtotal x (1+iva_linea)) debe cuadrar contra el NETO a pagar; tolerancia
  // de redondeo: 1 peso por linea. Si cuadra sin IVA (factura exenta) tambien es
  // silencio. Solo alerta cuando NO cuadra de ninguna de las dos formas.
  const sumaConIva = items.reduce((a, i) => a + subtotalConIva(i.subtotal, i.ivaPct), 0);
  const tolerancia = Math.max(1, items.length);
  const cuadraSinIva = totalFactura > 0 && Math.abs(sumaItems - totalFactura) <= tolerancia;
  const cuadraConIva = totalFactura > 0 && Math.abs(sumaConIva - totalFactura) <= tolerancia;
  let avisoTotal = '';
  if (totalFactura > 0) {
    if (cuadraConIva && !cuadraSinIva) {
      avisoTotal = `<div style="font-size:0.875rem;color:#3fb950;margin-top:6px">✓ Cuadra con el IVA incluido: items ${fmtCOP(sumaItems)} + IVA = ${fmtCOP(Math.round(sumaConIva))} ≈ total ${fmtCOP(totalFactura)}.</div>`;
    } else if (!cuadraSinIva && !cuadraConIva) {
      avisoTotal = `<div style="font-size:0.875rem;color:#d29922;margin-top:6px">⚠️ La suma de los items no cuadra con el total de la factura ni siquiera sumando el IVA (items ${fmtCOP(sumaItems)}, con IVA ${fmtCOP(Math.round(sumaConIva))}, total ${fmtCOP(totalFactura)}). Puede ser un descuento o un item mal leído — avísale al administrador.</div>`;
    }
    // cuadraSinIva: silencio (factura sin IVA o items ya con IVA incluido).
  }

  // COSTO-ALERTA-P-T1: resumen de subidas (la alerta local inmediata; la
  // notificacion push al propietario viaja por el rail unico #5656 cuando la
  // bandeja APR exista).
  const subidas = items.filter(i => {
    const pu = costoUnitConIva(i.subtotal, i.ivaPct, i.cantidad);
    return pu > 0 && i.costoRef > 0 && (pu - i.costoRef) / i.costoRef * 100 >= 2;
  }).length;
  const avisoCostos = subidas > 0
    ? `<div style="font-size:0.875rem;color:#d29922;margin-top:6px">💸 ${subidas} producto${subidas === 1 ? ' subió' : 's subieron'} de precio vs tu última compra — revisa las líneas en ámbar antes de aprobar.</div>`
    : '';

  const html = `
    <div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;max-height:300px;overflow-y:auto">
      ${items.map((it, i) => lineaItemHtml(it, i)).join('')}
    </div>
    ${avisoCostos}
    ${avisoTotal}
    <div style="display:flex;gap:8px;margin-top:10px">
      <button type="button" class="ka-ver-later" style="flex:1;padding:10px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#8b949e;cursor:pointer">Ahora no</button>
      <button type="button" class="ka-ver-ok" style="flex:2;padding:10px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">✅ Todo correcto, aprobar</button>
    </div>`;

  const $bubble = appendAgent($messages,
    `Revisa lo que entendí de tu factura. Si algo no cuadra, tócalo y lo corregimos:${html}`);

  const cerrar = () => {
    $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
  };

  $bubble.querySelectorAll('.ka-ver-fix').forEach(btn => {
    btn.addEventListener('click', () => {
      cerrar();
      const idx = Number(btn.dataset.idx);
      appendUser($messages, `✎ Corregir ${esc(items[idx].nombre)}`);
      renderCorreccionItem($messages, businessId, facturaId, operador, factura, items, idx);
    });
  });

  // OCR-FLUJO-COLAB-T1: selector de insumo por producto.
  $bubble.querySelectorAll('.ka-ver-match').forEach(btn => {
    btn.addEventListener('click', () => {
      cerrar();
      const idx = Number(btn.dataset.idx);
      appendUser($messages, `🔗 Elegir insumo para ${esc(items[idx].nombre)}`);
      renderSelectorInsumo($messages, businessId, facturaId, operador, factura, items, idx);
    });
  });

  // COMPRAS-BORRADOR-ANULACION-T1: Sin inventario se decide aqui y se guarda al aprobar, junto con los insumos elegidos.
  $bubble.querySelectorAll('.ka-ver-sininv').forEach(btn => {
    btn.addEventListener('click', () => {
      cerrar();
      const idx = Number(btn.dataset.idx);
      const it = items[idx];
      it.sinInventario = !it.sinInventario;
      it.sinInventarioCambiado = true;
      appendUser($messages, `${it.sinInventario ? 'Sin inventario' : 'Quitar sin inventario'}: ${esc(it.nombre)}`);
      renderVerificacionResumen($messages, businessId, facturaId, operador, factura, items);
    });
  });

  $bubble.querySelector('.ka-ver-later').addEventListener('click', () => {
    cerrar();
    appendUser($messages, 'Ahora no');
    appendAgent($messages,
      `Sin problema. La factura queda <b>pendiente de verificación</b>; puedes revisarla luego o pedirle al administrador que la apruebe. ¡Gracias ${esc(firstName(operador?.nombre))}! 🙌`);
  });

  $bubble.querySelector('.ka-ver-ok').addEventListener('click', async () => {
    cerrar();
    appendUser($messages, '✅ Todo correcto, aprobar');
    // OCR-FLUJO-COLAB-T1 paso 3: confirmacion EXPLICITA del total antes de todo.
    pasoConfirmarTotal($messages, businessId, facturaId, operador, factura, items);
  });
}

/**
 * OCR-FLUJO-COLAB-T1: selector de insumo en el chat. Candidatos del OCR primero
 * (con score), busqueda del catalogo debajo. Anti-lecciones aplicadas: nada de
 * <label> envolvente (ADMIN-UX-T1: roba el foco) y el input JAMAS se reconstruye
 * (solo la lista de resultados se actualiza).
 */
function renderSelectorInsumo($messages, businessId, facturaId, operador, factura, items, idx) {
  const it = items[idx];
  const candidatosHtml = (it.candidatos || []).length > 0
    ? `<div style="font-size:0.875rem;color:#8b949e;margin-bottom:4px">Sugerencias del sistema:</div>` +
      it.candidatos.slice(0, 4).map(c => `
        <button type="button" class="ka-sel-cand" data-id="${esc(c.insumo_id)}" data-nombre="${esc(c.nombre)}"
          style="display:block;width:100%;text-align:left;background:#161b22;border:1px solid #30363d;border-radius:6px;color:#79c0ff;padding:7px 10px;font-size:0.875rem;cursor:pointer;margin-bottom:4px">
          ${esc(c.nombre)}${c.presentacion ? ` <span style="color:#8b949e">${esc(c.presentacion)}</span>` : ''}${typeof c.score === 'number' ? ` <span style="color:#8b949e">(${Math.round(c.score * 100)}%)</span>` : ''}
        </button>`).join('')
    : `<div style="font-size:0.875rem;color:#8b949e;font-style:italic;margin-bottom:4px">El sistema no tiene sugerencias — búscalo abajo:</div>`;

  const $bubble = appendAgent($messages,
    `¿Cuál insumo del catálogo es <b>${esc(it.nombre)}</b>?
    <div style="margin-top:8px">${candidatosHtml}</div>
    <div style="margin-top:8px">
      <input type="search" class="ka-sel-buscar" placeholder="Buscar en el catálogo… (mín 2 letras)"
        style="width:100%;padding:8px 10px;background:#0d1117;border:1px solid #30363d;border-radius:6px;color:#e1e4e8;font-size:0.875rem">
      <div class="ka-sel-resultados" style="margin-top:4px;display:none;max-height:150px;overflow-y:auto;border:1px solid #30363d;border-radius:6px;background:#161b22"></div>
    </div>
    <button type="button" class="ka-sel-volver" style="margin-top:8px;padding:7px 12px;background:transparent;border:1px solid #30363d;border-radius:6px;color:#8b949e;font-size:0.875rem;cursor:pointer">← Volver sin elegir</button>`);

  const $buscar = $bubble.querySelector('.ka-sel-buscar');
  const $resultados = $bubble.querySelector('.ka-sel-resultados');

  const elegir = (id, nombre) => {
    it.insumoId = id;
    it.nombre = nombre;
    it.matchCorregido = true;
    // Casar a un insumo quita la marca (la API hace lo mismo): no se manda sin inventario de un item casado.
    if (it.sinInventario) { it.sinInventario = false; it.sinInventarioCambiado = false; }
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, `→ ${esc(nombre)}`);
    appendAgent($messages, `Listo, quedó como <b>${esc(nombre)}</b> 🧠 y lo voy a recordar para la próxima factura de este proveedor.`);
    renderVerificacionResumen($messages, businessId, facturaId, operador, factura, items);
  };

  $bubble.querySelectorAll('.ka-sel-cand').forEach(b =>
    b.addEventListener('click', () => elegir(b.dataset.id, b.dataset.nombre)));

  let selTimer = null, selSeq = 0;
  $buscar.addEventListener('input', () => {
    const q = $buscar.value.trim();
    if (selTimer) clearTimeout(selTimer);
    if (q.length < 2) { $resultados.style.display = 'none'; return; }
    const mySeq = ++selSeq;
    selTimer = setTimeout(async () => {
      $resultados.innerHTML = `<div style="padding:8px 10px;color:#8b949e;font-size:0.875rem">↻ buscando…</div>`;
      $resultados.style.display = 'block';
      const res = await InsumosApiClient.searchInsumos(businessId, { q, page: 1, pageSize: 20 });
      if (mySeq !== selSeq) return;
      const lista = res.ok ? (res.data?.items || res.data?.data?.items || []) : [];
      if (lista.length === 0) {
        $resultados.innerHTML = `<div style="padding:8px 10px;color:#8b949e;font-size:0.875rem;font-style:italic">Sin resultados para "${esc(q)}".</div>`;
        return;
      }
      $resultados.innerHTML = lista.map(r => `
        <button type="button" class="ka-sel-res" data-id="${esc(r.id || r.insumo_id)}" data-nombre="${esc(r.nombre)}"
          style="display:block;width:100%;text-align:left;background:transparent;border:none;border-bottom:1px solid #21262d;color:#c9d1d9;padding:7px 10px;font-size:0.875rem;cursor:pointer">
          ${esc(r.nombre)}${r.grupo ? ` <span style="color:#a371f7;font-size:0.875rem">${esc(r.grupo)}</span>` : ''}
        </button>`).join('');
      $resultados.querySelectorAll('.ka-sel-res').forEach(b =>
        b.addEventListener('click', () => elegir(b.dataset.id, b.dataset.nombre)));
    }, 300);
  });

  $bubble.querySelector('.ka-sel-volver').addEventListener('click', () => {
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    renderVerificacionResumen($messages, businessId, facturaId, operador, factura, items);
  });

  setTimeout(() => $buscar.focus(), 50);
}

/**
 * OCR-FLUJO-COLAB-T1 paso 3: el colaborador confirma el VALOR TOTAL como paso
 * explicito (antes la aprobacion asumia el total del OCR sin preguntar).
 */
function pasoConfirmarTotal($messages, businessId, facturaId, operador, factura, items) {
  const total = Number(factura?.total) || 0;
  const $bubble = appendAgent($messages,
    `Último chequeo: el total de la factura es <b style="font-size:1.05rem">${fmtCOP(total)}</b>. ¿Coincide con el papel?
    <div style="display:flex;gap:8px;margin-top:10px">
      <button type="button" class="ka-tot-no" style="flex:1;padding:9px;background:transparent;border:1px solid #d29922;border-radius:8px;color:#d29922;font-size:0.875rem;cursor:pointer">No coincide</button>
      <button type="button" class="ka-tot-si" style="flex:2;padding:9px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">Sí, es ${fmtCOP(total)}</button>
    </div>`);
  const desactivar = () => { $bubble.querySelectorAll('button').forEach(b => b.disabled = true); $bubble.style.opacity = '0.6'; };
  $bubble.querySelector('.ka-tot-si').addEventListener('click', async () => {
    desactivar();
    appendUser($messages, `Sí, el total es ${fmtCOP(total)}`);
    await enviarVerificacion($messages, businessId, facturaId, operador, items, total);
  });
  $bubble.querySelector('.ka-tot-no').addEventListener('click', () => {
    desactivar();
    appendUser($messages, 'El total no coincide');
    appendAgent($messages,
      `Mejor no la aprobamos así. 📸 Revisa si el papel dice otro valor y avísale al administrador para corregir el total desde el editor — la factura queda <b>pendiente</b>.`);
  });
}

// Stepper de correccion: cantidad de la factura -> factor. Mismo patron de
// credibilidad que el flujo de movimientos (un paso por burbuja, sin modales).
function renderCorreccionItem($messages, businessId, facturaId, operador, factura, items, idx) {
  const it = items[idx];
  const html = `
    <div style="margin-top:6px;display:flex;flex-direction:column;gap:8px">
      <label style="font-size:0.875rem;color:#8b949e">¿Cuánto dice la factura?
        <input type="text" inputmode="decimal" class="ka-ver-cf" value="${it.cantFactura ?? ''}" placeholder="ej: 5"
          style="width:100%;margin-top:4px;padding:9px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:0.95rem;text-align:center;box-sizing:border-box">
      </label>
      <label style="font-size:0.875rem;color:#8b949e">¿Cuántas unidades trae cada una?
        <input type="text" inputmode="decimal" class="ka-ver-fx" value="${it.factor ?? ''}" placeholder="ej: 8 (una caja de 8)"
          style="width:100%;margin-top:4px;padding:9px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:0.95rem;text-align:center;box-sizing:border-box">
      </label>
      <div class="ka-ver-preview" style="font-size:0.875rem;color:#8b949e;min-height:34px"></div>
      <div style="display:flex;gap:8px">
        <button type="button" class="ka-ver-cancel" style="flex:1;padding:9px;background:#161b22;border:1px solid #30363d;border-radius:8px;color:#8b949e;cursor:pointer">Cancelar</button>
        <button type="button" class="ka-ver-apply" style="flex:1;padding:9px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">Guardar</button>
      </div>
    </div>`;

  const $bubble = appendAgent($messages, `<b>${esc(it.nombre)}</b> — total de la línea ${fmtCOP(it.subtotal)} (ese no se toca).${html}`);
  const $cf = $bubble.querySelector('.ka-ver-cf');
  const $fx = $bubble.querySelector('.ka-ver-fx');
  const $prev = $bubble.querySelector('.ka-ver-preview');

  const preview = () => {
    const cant = cantidadDerivada(parsearNumeroColombiano($cf.value), parsearNumeroColombiano($fx.value));
    if (cant == null) { $prev.textContent = ''; return null; }
    const pu = precioUnitAnclado(it.subtotal, cant);
    // OCR-IVA-T1: al colaborador se le muestra el costo REAL (con IVA); el pu
    // documental (sin IVA) sigue siendo el que viaja al backend en el estado.
    const puIva = costoUnitConIva(it.subtotal, it.ivaPct, cant);
    const costoLinea = puIva != null
      ? `Costo real por unidad: <b>${fmtCOP(puIva)}</b>${it.ivaPct > 0 ? ` (IVA ${fmtQty(it.ivaPct)}% incluido)` : ''}`
      : (pu != null ? `Costo por unidad: ${fmtCOP(pu)}` : '');
    $prev.innerHTML = `<span style="color:#3fb950">Entrarían <b>${fmtQty(cant)}${it.unidadInv ? ' ' + esc(it.unidadInv) : ''}</b> al inventario</span>` +
      (costoLinea ? `<div style="color:#8b949e">${costoLinea}</div>` : '');
    return { cant, pu };
  };
  $cf.addEventListener('input', preview);
  $fx.addEventListener('input', preview);
  preview();

  const volver = () => {
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    renderVerificacionResumen($messages, businessId, facturaId, operador, factura, items);
  };

  $bubble.querySelector('.ka-ver-cancel').addEventListener('click', () => {
    appendUser($messages, 'Cancelar');
    volver();
  });

  $bubble.querySelector('.ka-ver-apply').addEventListener('click', () => {
    const calc = preview();
    if (!calc) {
      $cf.style.borderColor = '#f85149'; $fx.style.borderColor = '#f85149';
      $prev.innerHTML = `<span style="color:#f85149">Escribe los dos números (mayores que cero) para poder calcular.</span>`;
      return;
    }
    it.cantFactura = parsearNumeroColombiano($cf.value);
    it.factor = parsearNumeroColombiano($fx.value);
    it.cantidad = calc.cant;
    if (calc.pu != null) it.precioUnit = calc.pu;
    it.corregido = true;
    appendUser($messages, `${fmtQty(it.cantFactura)} × ${fmtQty(it.factor)} = ${fmtQty(it.cantidad)}`);
    volver();
  });

  setTimeout(() => $cf.focus(), 50);
}

/**
 * OCR-DUP-1 (COORD #5438, caso real 2026-08-11): el cajero registro a mano un
 * egreso (Coca Cola $160.600) y el pipeline OCR creo el suyo por el MISMO monto
 * — doble captura silenciosa. Antes de aprobar, buscar egresos de HOY (dia
 * operativo Bogota) con el mismo monto que NO vengan del pipeline OCR (esos
 * arrancan con "Compra ", incluido el de esta misma factura) ni esten anulados.
 * FAIL-OPEN: si el endpoint falla o no existe, la aprobacion sigue — el chequeo
 * es auxiliar y jamas debe bloquear la operacion.
 */
async function buscarPosiblesDoblesCapturas(businessId, totalFactura) {
  try {
    const total = Number(totalFactura) || 0;
    if (total <= 0) return [];
    const hoy = fechaOperativaBogota();
    const res = await FinanzasApiClient.listEgresos(businessId, { from: hoy, to: hoy, page: 1, pageSize: 100 });
    if (!res.ok) return [];
    const payload = res.data?.data ?? res.data ?? {};
    const lista = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
    return lista.filter(e => {
      const estado = String(e.estado || '').toLowerCase();
      if (estado.startsWith('anulad')) return false;
      // DUP-FID (STOP #5658): la exclusion del pipeline OCR ya NO depende del
      // texto del concepto (que va a cambiar a apodo/BADUR con el contrato
      // #5599) sino de la IDENTIDAD: el egreso OCR tiene factura_id, el manual
      // no. El DTO lo expone (BusinessEgresosDtos: source derivado por
      // factura_id != null). Esto destraba el A2 de Ares sin regresion.
      if (e.factura_id != null && e.factura_id !== '') return false;
      return Math.abs((Number(e.total) || 0) - total) < 1;
    });
  } catch { return []; }
}

async function enviarVerificacion($messages, businessId, facturaId, operador, items, totalFactura) {
  // OCR-DUP-1: aviso ANTES de aprobar si hoy ya hay un egreso manual del mismo monto.
  const dups = await buscarPosiblesDoblesCapturas(businessId, totalFactura);
  if (dups.length > 0) {
    const filas = dups.map(e => {
      const hora = String(e.hora_operativa || e.hora || '').slice(0, 5);
      return `<div style="margin-top:6px;padding:8px 10px;background:#0d1117;border:1px solid #d2992255;border-radius:6px;font-size:0.875rem">
        «${esc(e.concepto || 'sin concepto')}» — <b>${fmtCOP(Number(e.total) || 0)}</b>${hora ? ` · ${esc(hora)}` : ''}${e.usuario ? ` · ${esc(e.usuario)}` : ''}
      </div>`;
    }).join('');
    const $bubble = appendAgent($messages,
      `⚠️ <b>Espera un momento:</b> hoy ya ${dups.length === 1 ? 'se registró a mano un egreso' : `hay ${dups.length} egresos manuales`} por el <b>mismo monto</b> de esta factura:` +
      filas +
      `<div style="font-size:0.875rem;color:#8b949e;margin-top:8px">Puede que esta compra ya esté cobrada dos veces. Si apruebas igual, avísale al administrador para que anule el que sobre (desde Desktop).</div>` +
      `<div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap">
        <button type="button" class="ka-dup-seguir" style="padding:6px 14px;background:#238636;border:none;border-radius:6px;color:#fff;font-size:0.875rem;font-weight:600;cursor:pointer">Aprobar de todos modos</button>
        <button type="button" class="ka-dup-frenar" style="padding:6px 14px;background:transparent;border:1px solid #30363d;border-radius:6px;color:#c9d1d9;font-size:0.875rem;cursor:pointer">Mejor no aprobar aún</button>
      </div>`);
    const desactivar = () => $bubble.querySelectorAll('.ka-dup-seguir,.ka-dup-frenar').forEach(b => { b.disabled = true; b.style.opacity = '0.5'; b.style.cursor = 'default'; });
    $bubble.querySelector('.ka-dup-seguir').addEventListener('click', async () => {
      desactivar();
      appendUser($messages, 'Aprobar de todos modos');
      await ejecutarAprobacion($messages, businessId, facturaId, operador, items);
    });
    $bubble.querySelector('.ka-dup-frenar').addEventListener('click', () => {
      desactivar();
      appendUser($messages, 'Mejor no aprobar aún');
      appendAgent($messages, `Buena decisión 👍 La factura queda <b>pendiente</b>. Cuéntale al administrador del egreso repetido para que resuelva cuál queda.`);
    });
    return;
  }
  await ejecutarAprobacion($messages, businessId, facturaId, operador, items);
}

async function ejecutarAprobacion($messages, businessId, facturaId, operador, items) {
  // OCR-FLUJO-COLAB-T1 paso 4: los matches que el colaborador eligio viajan por
  // el MISMO path del editor admin (PATCH item con insumo_id + MATCH) — los
  // hooks del backend aprenden el alias al reconciliar, cero logica nueva aqui.
  const conMatchNuevo = items.filter(i => i.matchCorregido && i.insumoId);
  if (conMatchNuevo.length > 0) {
    const $t = appendTyping($messages, `Guardando ${conMatchNuevo.length} match${conMatchNuevo.length === 1 ? '' : 'es'}…`);
    for (const it of conMatchNuevo) {
      const r = await ComprasApiClient.updateItem(businessId, facturaId, it.id, { insumo_id: it.insumoId, match_status: 'MATCH' });
      // Fail-loud suave: un match que no persiste NO bloquea la aprobacion
      // (el admin lo puede rematar), pero el colaborador se entera.
      if (!r.ok) appendAgent($messages, `⚠️ No pude guardar el match de <b>${esc(it.nombre)}</b> — díselo al administrador para que lo remate en el editor.`);
    }
    $t.remove();
  }
  const conSinInv = items.filter(i => i.sinInventarioCambiado && !i.insumoId);
  if (conSinInv.length > 0) {
    const $t = appendTyping($messages, 'Guardando los items sin inventario…');
    for (const it of conSinInv) {
      const r = await ComprasApiClient.updateItem(businessId, facturaId, it.id, { sin_inventario: !!it.sinInventario });
      if (r.ok) it.sinInventarioCambiado = false;
      else appendAgent($messages, `⚠️ No pude guardar <b>${esc(it.nombre)}</b> como sin inventario: ${esc(textoErrorSinInventario(errorMessage(r, '')))} Díselo al administrador.`);
    }
    $t.remove();
  }
  const $typing = appendTyping($messages, 'Aprobando…');
  // Solo viajan los items que el colaborador toco. Si no corrigio nada mandamos
  // items:[] -- el contrato #3362 lo declara valido y es el caso comun.
  const corregidos = items.filter(i => i.corregido).map(i => ({
    id: i.id, cantidadFactura: i.cantFactura, factorEmpaque: i.factor
  }));
  const res = await ComprasApiClient.verificarFactura(businessId, facturaId, { aprobar: true, items: corregidos });
  $typing.remove();

  if (res.ok) {
    // FV-T5b (bit #3382): itemsCorregidos lo confirma el BACKEND (puede diferir de
    // lo que mandamos si algun item no aplicaba), asi que se prefiere sobre el
    // conteo local.
    const n = res.data?.items_corregidos ?? corregidos.length;
    const aprendidos = res.data?.alias_actualizados || 0;
    // El aprendizaje de alias es lo que le ahorra el trabajo la proxima vez: vale
    // la pena decirselo, es la recompensa de haberse tomado el tiempo de corregir.
    const aprendizaje = aprendidos > 0
      ? `<br><span style="color:#3fb950">🧠 Aprendí el empaque: la próxima factura de este proveedor ya te llega con la cantidad correcta.</span>`
      : '';
    appendAgent($messages,
      `✅ ¡Factura verificada! ${n > 0 ? `Corregiste ${n} ${n === 1 ? 'producto' : 'productos'} y ya ` : 'Ya '}` +
      `quedó lista para entrar al inventario. ¡Gracias ${esc(firstName(operador?.nombre))}! 🙌${aprendizaje}`);
    return;
  }

  // MV-T2b (bit #3442): esta deteccion estaba MAL desde FV-T4 y es bug propio.
  // Miraba res.status / res.error.status, pero ApiBase usa fail(res.status, msg),
  // que deja el codigo HTTP en `code` y el mensaje en `error` (string). O sea que
  // el 409 caia al mensaje generico y el colaborador leia "no pude aprobar" cuando
  // en realidad la factura SI estaba verificada. Mi smoke de ayer no lo cazo porque
  // simulaba {status:409} en vez del shape real de fail(). errorStatus() mira los
  // tres sitios posibles y cierra el hueco.
  const status = errorStatus(res);

  // nexo-api 578 (D-139): un 409 ya no es siempre "alguien la aprobo primero". ITEMS_SIN_RESOLVER = faltan items por
  // casar o marcar sin inventario (la factura NO quedo aprobada); FACTURA_ANULADA = no se aprueba. Solo el resto de
  // 409 es el idempotente del contrato #3362.
  if (status === 409) {
    const rechazo = rechazoDeAprobar(res);
    if (rechazo.codigo === 'ITEMS_SIN_RESOLVER') {
      const lista = rechazo.items.map(i => `<div style="margin-top:4px;font-size:14px">• ${esc(i.descripcion || 'item sin nombre')}${i.sugerirSinInventario ? ' <span style="color:#8b949e">(parece un flete o un servicio)</span>' : ''}</div>`).join('');
      appendAgent($messages,
        `⚠️ <b>Todavía no se puede aprobar.</b> ${rechazo.items.length ? `Faltan ${rechazo.items.length === 1 ? 'este producto' : `estos ${rechazo.items.length} productos`}:` : 'Faltan productos por resolver.'}${lista}` +
        `<div style="font-size:14px;color:#8b949e;margin-top:6px">A cada uno elígele su insumo, o toca <b>Sin inventario</b> si es un flete o un servicio. La factura sigue pendiente.</div>`);
      return;
    }
    if (rechazo.codigo === 'FACTURA_ANULADA') {
      appendAgent($messages, '⛔ Esta factura fue <b>anulada</b>: ya no se aprueba. Si es un error, avísale al administrador.');
      return;
    }
    appendAgent($messages, '✅ Esta factura ya estaba verificada. Todo en orden. 🙌');
    return;
  }
  if (status === 0 || res.code === 'NETWORK' || res.code === 'TIMEOUT') {
    appendAgent($messages, '❌ No hay conexión ahora mismo. Tu factura quedó guardada; intenta aprobarla de nuevo en un momento. 🙏');
    return;
  }
  appendAgent($messages,
    `❌ No pude aprobar la factura. Queda <b>pendiente de aprobación del administrador</b>, él la revisa. 🙏` +
    `<div style="font-size:0.875rem;color:#6e7681;margin-top:4px">${esc(errorMessage(res))}</div>`);
}

function renderPorcionadoPicker($messages, businessId, operador, items) {
  const html = `
    <div style="display:flex;flex-direction:column;gap:6px;margin-top:8px;max-height:260px;overflow-y:auto">
      ${items.map(p => `<button type="button" class="ka-porc-item" data-fid="${esc(p.facturaItemId)}"
        style="text-align:left;padding:10px;background:#0d1117;border:1px solid #21262d;border-radius:8px;color:#c9d1d9;font-size:0.875rem;cursor:pointer">
        <b>${esc(p.insumoNombre)}</b><br>
        <span style="color:#8b949e;font-size:0.875rem">${fmtQty(p.gramos)}g comprados${p.numeroFactura ? ` · factura ${esc(p.numeroFactura)}` : ''}</span>
      </button>`).join('')}
    </div>`;
  const $bubble = appendAgent($messages,
    `Tienes <b>${items.length}</b> pendiente(s) de porcionar. Elige uno para registrar las porciones reales:${html}`);

  function pick(facturaItemId) {
    const item = items.find(p => p.facturaItemId === facturaItemId);
    if (!item) return;
    $bubble.querySelectorAll('button').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, `✅ ${esc(item.insumoNombre)}`);
    renderCantidadPorciones($messages, businessId, operador, item);
  }
  $bubble.querySelectorAll('.ka-porc-item').forEach(btn => {
    btn.addEventListener('click', () => pick(btn.dataset.fid));
  });
}

function renderCantidadPorciones($messages, businessId, operador, item) {
  const html = `
    <div style="margin-top:6px;display:flex;gap:8px;align-items:center">
      <input type="text" inputmode="numeric" class="ka-porc-cantidad" placeholder="0"
        style="flex:1;padding:10px;background:#0d1117;border:1px solid #30363d;border-radius:8px;color:#e1e4e8;font-size:1rem;text-align:center;box-sizing:border-box">
      <span style="color:#8b949e;font-size:0.875rem;white-space:nowrap">porciones</span>
      <button type="button" class="ka-porc-cant-ok" style="padding:10px 16px;background:#238636;border:none;border-radius:8px;color:#fff;font-weight:600;cursor:pointer">➜</button>
    </div>`;
  const $bubble = appendAgent($messages,
    `¿Cuántas porciones reales contaste de <b>${esc(item.insumoNombre)}</b> (${fmtQty(item.gramos)}g comprados)?${html}`);
  const $input = $bubble.querySelector('.ka-porc-cantidad');
  const $ok = $bubble.querySelector('.ka-porc-cant-ok');

  const submit = () => {
    const porciones = parsearNumeroColombiano($input.value);
    if (!(porciones > 0)) { $input.style.borderColor = '#f85149'; $input.focus(); return; }
    $bubble.querySelectorAll('button,input').forEach(b => b.disabled = true);
    $bubble.style.opacity = '0.6';
    appendUser($messages, `${fmtQty(porciones)} porciones`);
    registrarPorcionado($messages, businessId, operador, item, porciones);
  };
  $ok.addEventListener('click', submit);
  $input.addEventListener('keydown', (e) => { if (e.key === 'Enter') submit(); });
  setTimeout(() => $input.focus(), 50);
}

async function registrarPorcionado($messages, businessId, operador, item, porciones) {
  const $typing = appendTyping($messages, 'Registrando…');

  // Paso 1: movimiento REAL (ENTRADA con la cantidad en porciones) -- el mismo
  // POST del form manual/desperdicio, ya soportado desde #159.
  const now = new Date();
  const hora = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;
  const movRes = await ComprasApiClient.createMovimiento(businessId, {
    tipo: 'entrada',
    insumoId: item.insumoId,
    cantidad: porciones,
    fecha: todayISO(),
    hora,
    usuario: operador.nombre,
    motivo: `[Kiosk] Porcionado: ${fmtQty(porciones)} porciones de ${item.insumoNombre}${item.numeroFactura ? ` (factura ${item.numeroFactura})` : ''}`
  });

  if (!movRes.ok) {
    $typing.remove();
    appendAgent($messages, mensajeErrorMovimiento(movRes, 'La alerta sigue abierta.'));
    return;
  }

  // Paso 2: PATCH cierra la alerta + audita. Si esto falla, el movimiento YA
  // quedo registrado (paso 1 exitoso) -- se avisa honestamente en vez de fingir
  // exito total, la alerta podria seguir visible hasta reintentar el cierre.
  const cierreRes = await PorcionadoApiClient.cerrarAlerta(businessId, item.facturaItemId, {
    porcionesRegistradas: porciones,
    movimientoId: movRes.data?.id || null,
    usuario: operador.nombre
  });
  $typing.remove();

  if (cierreRes.ok) {
    appendAgent($messages,
      `✅ ¡Listo! Registré <b>${fmtQty(porciones)} porciones</b> de <b>${esc(item.insumoNombre)}</b>. ` +
      `¡Gracias ${esc(firstName(operador.nombre))}! 🙌`);
  } else {
    appendAgent($messages,
      `✅ El movimiento de <b>${fmtQty(porciones)} porciones</b> de <b>${esc(item.insumoNombre)}</b> quedó registrado, ` +
      `pero no pude cerrar la alerta (podría seguir apareciendo en la bandeja). Avísale al administrador. 🙏`);
  }
}

// ============== Burbujas de chat ==============

function scrollBottom($m) { $m.scrollTop = $m.scrollHeight; }

function appendUser($m, html) {
  const d = document.createElement('div');
  d.style.cssText = 'align-self:flex-end;max-width:80%;background:#1f6feb;color:#fff;padding:9px 13px;border-radius:14px 14px 2px 14px;font-size:0.88rem;line-height:1.4;word-break:break-word';
  d.innerHTML = html;
  $m.appendChild(d); scrollBottom($m); return d;
}

function appendAgent($m, html) {
  const w = document.createElement('div');
  w.style.cssText = 'align-self:flex-start;max-width:88%;display:flex;gap:8px;align-items:flex-start';
  w.innerHTML = `<div style="font-size:1.15rem;flex-shrink:0;line-height:1.3">🧙</div>` +
    `<div style="background:#161b22;border:1px solid #21262d;color:#c9d1d9;padding:9px 13px;border-radius:14px 14px 14px 2px;font-size:0.88rem;line-height:1.45;word-break:break-word">${html}</div>`;
  $m.appendChild(w); scrollBottom($m); return w;
}

function appendTyping($m, label = 'Escribiendo…') {
  const w = document.createElement('div');
  w.style.cssText = 'align-self:flex-start;display:flex;gap:8px;align-items:center;color:#6e7681;font-size:0.875rem';
  w.innerHTML = `<div style="font-size:1.15rem">🧙</div>` +
    `<div style="background:#161b22;border:1px solid #21262d;padding:8px 13px;border-radius:14px">${esc(label)}</div>`;
  $m.appendChild(w); scrollBottom($m); return w;
}
